import { RefObject, useEffect, useState } from 'react';

const HUBSPOT_HYDRATION_DELAY = 1500;

type CheckboxGroup = {
	id: string;
	label: string;
	required: boolean;
	options: { value: string; label: string }[];
};

export function useHubSpotCheckboxGroups(containerRef: RefObject<HTMLDivElement>, formHtmlCode: string, selectAllLabel: string, isMounted: boolean) {
	const [checkboxGroups, setCheckboxGroups] = useState<CheckboxGroup[]>([]);

	useEffect(() => {
		if (!isMounted) return;

		const container = containerRef.current;
		if (!container) return;

		let active = true;
		const pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

		function discoverAndProcessCheckboxGroups() {
			const form = container.querySelector('form');
			if (!form) return;

			const foundGroups = Array.from(form.querySelectorAll('.hsfc-CheckboxFieldGroup'));

			foundGroups.forEach((group) => {
				(group as HTMLElement).setAttribute('data-react-controlled', 'false');
				const checkboxOptions = group.querySelector('.hsfc-CheckboxFieldGroup__Options');
				if (checkboxOptions) {
					const optionsElement = checkboxOptions as HTMLElement;
					optionsElement.style.opacity = '0';
					optionsElement.style.position = 'absolute';
					optionsElement.style.height = '1px';
					optionsElement.style.width = '1px';
					optionsElement.style.overflow = 'hidden';
					optionsElement.style.clip = 'rect(0, 0, 0, 0)';
					optionsElement.style.pointerEvents = 'none';
				}
			});

			const processedGroups = foundGroups.map((group, index) => {
				const labelEl = group.querySelector('.hsfc-FieldLabel');
				const isRequired = !!labelEl?.querySelector('.hsfc-FieldLabel__RequiredIndicator');
				const checkboxOptions = Array.from(group.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')).map((input) => ({
					value: input.value,
					label: input.closest('label')?.textContent?.trim() ?? input.value,
				}));
				return {
					id: `checkbox-group-${index}`,
					label: labelEl?.textContent?.replace('*', '').trim() ?? '',
					required: isRequired,
					options: [{ value: '__all__', label: selectAllLabel }, ...checkboxOptions],
				};
			});

			if (active) {
				setCheckboxGroups(processedGroups);
			}
		}

		discoverAndProcessCheckboxGroups();

		const observer = new MutationObserver(() => {
			const timer = setTimeout(discoverAndProcessCheckboxGroups, HUBSPOT_HYDRATION_DELAY);
			pendingTimeouts.push(timer);
		});
		observer.observe(container, { childList: true, subtree: true });

		return () => {
			active = false;
			observer.disconnect();
			pendingTimeouts.forEach(clearTimeout);
		};
	}, [formHtmlCode, selectAllLabel, isMounted]);

	return checkboxGroups;
}
